import React, { useEffect, useState } from 'react';
import {
    Card,
    Row,
    Col,
    Input,
    Tag,
    Space,
    Typography,
    Empty,
    Badge,
    Breadcrumb,
    Modal
} from 'antd';
import {
    PlayCircleOutlined,
    SearchOutlined,
    ClockCircleOutlined,
    UserOutlined,
    HomeOutlined,
    BookOutlined,
    FileTextOutlined,
    DollarOutlined,
    TeamOutlined,
    SettingOutlined,
    FireOutlined,
    CloseOutlined
} from '@ant-design/icons';
import Footer from '../../components/Footer';
import NavBar from '../../components/NavBar';
import SideBar from '../../components/SideBar';
import { youtubeService } from '../../services/youtubeService';
// import { VideoCard } from './components/VideoCard';

const { Title, Text, Paragraph } = Typography;
const { Search } = Input;

// Couleurs de la charte
const PRIMARY_COLOR = '#242166';
const SECONDARY_COLOR = '#1890ff';
const SUCCESS_COLOR = '#52c41a';
const WARNING_COLOR = '#faad14';
const ERROR_COLOR = '#ff4d4f';
const BACKGROUND_LIGHT = '#f5f7fa';

const VideoTutorialsPage = () => {
    const [searchText, setSearchText] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [isVideoModalVisible, setIsVideoModalVisible] = useState(false);
    const [currentVideo, setCurrentVideo] = useState(null);
    const [error, setError] = useState(null);

    // Liste des tutoriels vidéo
    const [tutorials, setTutorials] = useState([
        {
            id: 1,
            title: 'Paramètres des taux de commission',
            description: 'Configurez les taux de commission pour les différents intermédiaires',
            youtubeId: 'WlpQea4TBbk',
            duration: '13:35',
            category: 'Paramètres',
            level: 'IT',
            views: 0
        },
        {
            id: 2,
            title: 'Déclaration de sinistre',
            youtubeId: 'WefyA0pc_gs',
            category: 'Sinistres',
            level: 'Gestionnaire Sinistre',
            views: 0,
            duration: '22:02',
            description: 'Apprenez à déclarer un sinistre étape par étape, avec les meilleures pratiques pour une gestion efficace.'
        }
    ]);

    const categories = [
        { name: 'Tous', value: 'all', icon: <BookOutlined />, color: PRIMARY_COLOR },
        { name: 'Démarrage', value: 'Démarrage', icon: <HomeOutlined />, color: SUCCESS_COLOR },
        { name: 'Gestion', value: 'Gestion', icon: <TeamOutlined />, color: SECONDARY_COLOR },
        { name: 'Contrats', value: 'Contrats', icon: <FileTextOutlined />, color: WARNING_COLOR },
        { name: 'Sinistres', value: 'Sinistres', icon: <UserOutlined />, color: ERROR_COLOR },
        { name: 'Comptabilité', value: 'Comptabilité', icon: <DollarOutlined />, color: SUCCESS_COLOR },
        { name: 'Rapports', value: 'Rapports', icon: <FileTextOutlined />, color: PRIMARY_COLOR },
        { name: 'Paramètres', value: 'Paramètres', icon: <SettingOutlined />, color: '#8c8c8c' }
    ];

    // Filtrer les tutoriels
    const filteredTutorials = tutorials.filter(tutorial => {
        const matchesSearch = tutorial.title.toLowerCase().includes(searchText.toLowerCase()) ||
            tutorial.description.toLowerCase().includes(searchText.toLowerCase());
        const matchesCategory = selectedCategory === 'all' || tutorial.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    const getLevelColor = (level) => {
        switch (level) {
            case 'Gestionnaire Sinistre': return SUCCESS_COLOR;
            case 'Intermédiaire': return WARNING_COLOR;
            case 'Avancé': return ERROR_COLOR;
            default: return PRIMARY_COLOR;
        }
    };

    const getCategoryColor = (category) => {
        const cat = categories.find(c => c.value === category);
        return cat ? cat.color : PRIMARY_COLOR;
    };

    const openVideoModal = (tutorial) => {
        setCurrentVideo(tutorial);
        setIsVideoModalVisible(true);
    };

    const closeVideoModal = () => {
        setIsVideoModalVisible(false);
        setCurrentVideo(null);
    };

    // Récupérer les stats YouTube au chargement du composant
    useEffect(() => {
        const fetchYoutubeStats = async () => {
            const initialTutorials = [
                {
                    id: 1,
                    title: 'Parametrage des taux de commission',
                    youtubeId: 'WlpQea4TBbk',
                    category: 'Paramètres',
                    level: 'IT'
                },
                {
                    id: 2,
                    title: 'Déclaration de sinistre',
                    youtubeId: 'WefyA0pc_gs',
                    category: 'Sinistres',
                    level: 'Gestionnaire Sinistre'
                }
            ];
            try {
                setError(null);


                // Récupérer les IDs de toutes les vidéos
                const videoIds = tutorials.map(t => t.youtubeId);

                // Appeler l'API YouTube
                const stats = await youtubeService.getMultipleVideosStats(videoIds);

                // Mettre à jour les tutoriels avec les vraies stats
                const updatedTutorials = tutorials.map(tutorial => ({
                    ...tutorial,
                    views: stats[tutorial.youtubeId]?.viewCount || 0,
                    likes: stats[tutorial.youtubeId]?.likeCount || 0
                }));

                setTutorials(updatedTutorials);
            } catch (err) {
                console.error('Erreur lors de la récupération des stats:', err);
                setError('Impossible de charger les statistiques YouTube');
                // Utiliser les données initiales sans stats
                setTutorials(initialTutorials);
            }

        };

        fetchYoutubeStats();
    }, []); // Se lance une seule fois au montage

    const VideoCard = ({ tutorial, showBadge = false }) => (
        <Card
            hoverable
            style={{
                borderRadius: '16px',
                boxShadow: '0 2px 8px rgba(36, 33, 102, 0.08)',
                border: 'none',
                overflow: 'hidden',
                height: '100%',
                transition: 'all 0.3s ease'
            }}
            bodyStyle={{ padding: 0 }}
            onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(36, 33, 102, 0.15)';
                e.currentTarget.style.transform = 'translateY(-4px)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(36, 33, 102, 0.08)';
                e.currentTarget.style.transform = 'translateY(0)';
            }}
        >
            {/* Thumbnail YouTube */}
            <div style={{
                position: 'relative',
                paddingTop: '56.25%',
                background: `linear-gradient(135deg, ${getCategoryColor(tutorial.category)}20 0%, ${getCategoryColor(tutorial.category)}40 100%)`,
                cursor: 'pointer'
            }}
                onClick={() => openVideoModal(tutorial)}
            >
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `url(https://img.youtube.com/vi/${tutorial.youtubeId}/maxresdefault.jpg) center/cover`,
                    backgroundSize: 'cover'
                }}>
                    <div style={{
                        width: '72px',
                        height: '72px',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
                        transition: 'all 0.3s ease'
                    }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.1)';
                            e.currentTarget.style.backgroundColor = ERROR_COLOR;
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
                        }}
                    >
                        <PlayCircleOutlined style={{ fontSize: '44px', color: ERROR_COLOR }} />
                    </div>
                </div>
                <div style={{
                    position: 'absolute',
                    bottom: '12px',
                    right: '12px',
                    background: 'rgba(0,0,0,0.75)',
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: 'bold',
                    backdropFilter: 'blur(4px)'
                }}>
                    <ClockCircleOutlined style={{ marginRight: '6px' }} />
                    {tutorial.duration}
                </div>
            </div>

            {/* Contenu */}
            <div style={{ padding: '20px' }}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Space wrap>
                        <Tag color={getCategoryColor(tutorial.category)} style={{ borderRadius: '8px', fontWeight: 500 }}>
                            {tutorial.category}
                        </Tag>
                        <Tag color={getLevelColor(tutorial.level)} style={{ borderRadius: '8px' }}>
                            {tutorial.level}
                        </Tag>
                    </Space>

                    <Title level={5} style={{ margin: '8px 0', color: PRIMARY_COLOR }}>
                        {tutorial.title}
                    </Title>

                    <Text type="secondary" style={{ fontSize: '13px' }}>
                        {tutorial.description}
                    </Text>

                    <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #f0f0f0' }}>
                        <Space split={<span style={{ color: '#d9d9d9' }}>•</span>}>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                <PlayCircleOutlined style={{ marginRight: '4px' }} />
                                {tutorial.views} vues
                            </Text>
                        </Space>
                    </div>
                </Space>
            </div>
        </Card>
    );

    return (
        <div className="layout-wrapper layout-content-navbar  ">
            <div className="layout-container">
                <SideBar />

                {/* <!-- Layout container --> */}
                <div className="layout-page">
                    <NavBar />

                    {/* <!-- Content wrapper --> */}
                    <div className="content-wrapper">
                        {/* <!-- Content --> */}
                        <div className="container-xxl flex-grow-1 container-p-y">
                            <div style={{
                                background: BACKGROUND_LIGHT,
                                minHeight: '100vh',
                                padding: '24px'
                            }}>
                                {/* En-tête avec gradient */}
                                <Card
                                    style={{
                                        borderRadius: '16px',
                                        marginBottom: '32px',
                                        boxShadow: '0 4px 12px rgba(36, 33, 102, 0.1)',
                                        border: 'none',
                                        background: `linear-gradient(135deg, ${PRIMARY_COLOR} 0%, ${SECONDARY_COLOR} 100%)`
                                    }}
                                    bodyStyle={{ padding: '48px 32px' }}
                                >
                                    <Row align="middle" gutter={[24, 24]}>
                                        <Col xs={24} md={16}>
                                            <Space direction="vertical" size="small">
                                                <Breadcrumb
                                                    items={[
                                                        { title: <span style={{ color: 'rgba(255,255,255,0.8)' }}><HomeOutlined /> Accueil</span> },
                                                        { title: <span style={{ color: 'white' }}>Tutoriels</span> }
                                                    ]}
                                                    style={{ marginBottom: '8px' }}
                                                />
                                                <Title level={1} style={{ color: 'white', margin: 0, fontSize: '36px' }}>
                                                    <BookOutlined style={{ marginRight: '12px' }} />
                                                    Centre de Formation
                                                </Title>
                                                <Paragraph style={{ color: 'rgba(255,255,255,0.9)', fontSize: '16px', margin: 0, maxWidth: '600px' }}>
                                                    Découvrez nos tutoriels vidéo pour maîtriser toutes les fonctionnalités de la plateforme.
                                                    Des guides pas à pas pour tous les niveaux.
                                                </Paragraph>
                                            </Space>
                                        </Col>
                                        <Col xs={24} md={8}>
                                            <div style={{
                                                background: 'rgba(255,255,255,0.1)',
                                                backdropFilter: 'blur(10px)',
                                                borderRadius: '16px',
                                                padding: '24px',
                                                textAlign: 'center'
                                            }}>
                                                <Title level={2} style={{ color: 'white', margin: 0, fontSize: '42px' }}>
                                                    {tutorials.length}
                                                </Title>
                                                <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '16px' }}>
                                                    Tutoriels disponibles
                                                </Text>
                                            </div>
                                        </Col>
                                    </Row>
                                </Card>

                                {/* Barre de recherche */}
                                <Card
                                    style={{
                                        borderRadius: '16px',
                                        marginBottom: '24px',
                                        boxShadow: '0 2px 8px rgba(36, 33, 102, 0.08)',
                                        border: 'none'
                                    }}
                                    bodyStyle={{ padding: '24px' }}
                                >
                                    <Search
                                        placeholder="Rechercher un tutoriel..."
                                        allowClear
                                        enterButton="Rechercher"
                                        size="large"
                                        prefix={<SearchOutlined style={{ color: PRIMARY_COLOR }} />}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        style={{ maxWidth: '600px' }}
                                    />
                                </Card>

                                {/* Filtres par catégorie */}
                                <Card
                                    style={{
                                        borderRadius: '16px',
                                        marginBottom: '32px',
                                        boxShadow: '0 2px 8px rgba(36, 33, 102, 0.08)',
                                        border: 'none'
                                    }}
                                    bodyStyle={{ padding: '24px' }}
                                >
                                    <Text strong style={{ display: 'block', marginBottom: '16px', fontSize: '16px', color: PRIMARY_COLOR }}>
                                        Catégories
                                    </Text>
                                    <Space wrap size={[12, 12]}>
                                        {categories.map(category => (
                                            <Tag
                                                key={category.value}
                                                icon={category.icon}
                                                onClick={() => setSelectedCategory(category.value)}
                                                style={{
                                                    padding: '8px 16px',
                                                    fontSize: '14px',
                                                    borderRadius: '20px',
                                                    cursor: 'pointer',
                                                    border: selectedCategory === category.value ? `2px solid ${category.color}` : '1px solid #d9d9d9',
                                                    backgroundColor: selectedCategory === category.value ? `${category.color}15` : 'white',
                                                    color: selectedCategory === category.value ? category.color : '#595959',
                                                    fontWeight: selectedCategory === category.value ? 600 : 400,
                                                    transition: 'all 0.3s ease'
                                                }}
                                            >
                                                {category.name}
                                            </Tag>
                                        ))}
                                    </Space>
                                </Card>

                                {/* Tutoriels populaires */}
                                {selectedCategory === 'all' && tutorials.filter(t => t.popular).length > 0 && (
                                    <div style={{ marginBottom: '32px' }}>
                                        <Title level={3} style={{ color: PRIMARY_COLOR, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <FireOutlined style={{ color: ERROR_COLOR }} />
                                            Tutoriels populaires
                                        </Title>
                                        <Row gutter={[24, 24]}>
                                            {tutorials.filter(t => t.popular).map(tutorial => (
                                                <Col xs={24} sm={12} lg={8} key={tutorial.id}>
                                                    <Badge.Ribbon text="Populaire" color={ERROR_COLOR}>
                                                        <VideoCard tutorial={tutorial} showBadge={true} getCategoryColor={getCategoryColor} getLevelColor={getLevelColor} openVideoModal={openVideoModal} />
                                                    </Badge.Ribbon>
                                                </Col>
                                            ))}
                                        </Row>
                                    </div>
                                )}

                                {/* Grille des tutoriels */}
                                <div>
                                    <Title level={3} style={{ color: PRIMARY_COLOR, marginBottom: '16px' }}>
                                        {selectedCategory === 'all' ? 'Tous les tutoriels' : `Tutoriels - ${selectedCategory}`}
                                    </Title>

                                    {filteredTutorials.length === 0 ? (
                                        <Card style={{ borderRadius: '16px', textAlign: 'center', padding: '48px' }}>
                                            <Empty
                                                description={
                                                    <Text type="secondary">
                                                        Aucun tutoriel trouvé pour votre recherche
                                                    </Text>
                                                }
                                            />
                                        </Card>
                                    ) : (
                                        <Row gutter={[24, 24]}>
                                            {filteredTutorials.map(tutorial => (
                                                <Col xs={24} sm={12} lg={8} key={tutorial.id}>
                                                    <VideoCard tutorial={tutorial} />
                                                </Col>
                                            ))}
                                        </Row>
                                    )}
                                </div>

                                {/* Modal de lecture vidéo */}
                                <Modal
                                    open={isVideoModalVisible}
                                    onCancel={closeVideoModal}
                                    footer={null}
                                    width="90%"
                                    style={{ top: 20, maxWidth: '1200px' }}
                                    closeIcon={<CloseOutlined style={{ fontSize: '20px', color: 'white' }} />}
                                    styles={{
                                        header: {
                                            background: PRIMARY_COLOR,
                                            borderBottom: 'none'
                                        },
                                        body: {
                                            padding: 0,
                                            background: '#000'
                                        }
                                    }}
                                    title={
                                        currentVideo && (
                                            <div style={{ color: 'white' }}>
                                                <Title level={4} style={{ color: 'white', margin: 0 }}>
                                                    {currentVideo.title}
                                                </Title>
                                                <Space style={{ marginTop: '8px' }}>
                                                    <Tag color={getCategoryColor(currentVideo.category)} style={{ borderRadius: '8px' }}>
                                                        {currentVideo.category}
                                                    </Tag>
                                                    <Tag color={getLevelColor(currentVideo.level)} style={{ borderRadius: '8px' }}>
                                                        {currentVideo.level}
                                                    </Tag>
                                                    <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>
                                                        <ClockCircleOutlined style={{ marginRight: '4px' }} />
                                                        {currentVideo.duration}
                                                    </Text>
                                                </Space>
                                            </div>
                                        )
                                    }
                                >
                                    {currentVideo && (
                                        <div style={{ position: 'relative', paddingTop: '56.25%', background: '#000' }}>
                                            <iframe
                                                style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: '100%',
                                                    height: '100%',
                                                    border: 'none'
                                                }}
                                                src={`https://www.youtube.com/embed/${currentVideo.youtubeId}?autoplay=1&rel=0&modestbranding=1`}
                                                title={currentVideo.title}
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                allowFullScreen
                                            />
                                        </div>
                                    )}
                                    {currentVideo && (
                                        <div style={{ padding: '24px', background: 'white' }}>
                                            <Title level={5} style={{ color: PRIMARY_COLOR, marginBottom: '12px' }}>
                                                Description
                                            </Title>
                                            <Paragraph style={{ fontSize: '14px', color: '#595959', marginBottom: '16px' }}>
                                                {currentVideo.description}
                                            </Paragraph>
                                            <Space split={<span style={{ color: '#d9d9d9' }}>•</span>}>
                                                <Text type="secondary" style={{ fontSize: '13px' }}>
                                                    <PlayCircleOutlined style={{ marginRight: '4px' }} />
                                                    {currentVideo.views} vues
                                                </Text>
                                            </Space>
                                        </div>
                                    )}
                                </Modal>
                            </div>
                        </div>
                        {/* <!-- / Content --> */}

                        {/* <!-- Footer --> */}
                        <Footer />
                        {/* <!-- / Footer --> */}

                        <div className="content-backdrop fade"></div>
                    </div>
                    {/* <!-- Content wrapper --> */}
                </div>
                {/* <!-- / Layout page --> */}
            </div>

            {/* <!-- Overlay --> */}
            <div className="layout-overlay layout-menu-toggle"></div>

            {/* <!-- Drag Target Area To SlideIn Menu On Small Screens --> */}
            <div className="drag-target"></div>
        </div>
    );
};

export default VideoTutorialsPage;